import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ResolveReportModal from 'candidate_profile_redesign/modals/resolve_report_modal';
import { useCandidateProfile } from 'candidate_profile_redesign/store';
import type { FraudReport } from 'fraud/types';

type Props = {
  fraudReport: FraudReport | null;
  applicationId: number;
  onModalOpenReady: (openFn: () => void) => void;
};

/**
 * Wrapper component that bridges the talent matching context with the candidate profile
 * resolve fraud report modal. Synchronizes fraud report state between both contexts.
 */
const ResolveFraudReportModalWrapper = ({
  fraudReport,
  applicationId,
  onModalOpenReady,
}: Props) => {
  const queryClient = useQueryClient();
  const {
    actions: { useGlobalModal },
  } = useCandidateProfile();
  const { openModal, isOpen } = useGlobalModal('resolveFraudReportModal');
  const [prevIsOpen, setPrevIsOpen] = React.useState(false);

  // Expose openModal function through callback
  React.useEffect(() => {
    onModalOpenReady(() => {
      // Set initial fraud report data in candidate profile query cache
      if (fraudReport) {
        queryClient.setQueryData(['fraudReport'], fraudReport);
      }
      openModal();
    });
  }, [openModal, queryClient, fraudReport, onModalOpenReady]);

  // Sync back to talent matching when modal closes
  React.useEffect(() => {
    if (prevIsOpen && !isOpen) {
      // Modal just closed, sync the data
      const candidateProfileFraudReport = queryClient.getQueryData<FraudReport>(
        ['fraudReport']
      );
      if (candidateProfileFraudReport) {
        queryClient.setQueryData(
          ['fraudReport', applicationId],
          candidateProfileFraudReport
        );
      }
    }
    setPrevIsOpen(isOpen);
  }, [isOpen, prevIsOpen, queryClient, applicationId]);

  return <ResolveReportModal />;
};

export default ResolveFraudReportModalWrapper;
